import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const Signup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    company: '',
    role: '',
    country: '',
    phone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1);
  const [debugInfo, setDebugInfo] = useState('');
  const [emailValidation, setEmailValidation] = useState({ isValid: true, message: '' });
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const auth = useAuth();

  // Email confirmation handler
  useEffect(() => {
    const needsConfirmation = searchParams.get('confirm') === 'true';
    const provider = searchParams.get('provider');
    const locationMessage = location.state?.message;
    const locationEmail = location.state?.email;

    if (needsConfirmation) {
      if (provider === 'google') {
        setSuccess('🎉 Google account created successfully! You are now signed in and can access your dashboard.');
      } else {
        setSuccess('📧 Please check your email and click the confirmation link to activate your account.');
      }
    } else if (locationMessage) {
      setSuccess(locationMessage);
      if (locationEmail) {
        setFormData(prev => ({ ...prev, email: locationEmail.toLowerCase().trim() }));
      }
    }
  }, [searchParams, location.state]);

  const BLOCKED_DOMAINS = [
    '10minutemail.com', 'tempmail.org', 'guerrillamail.com', 'mailinator.com',
    'getnada.com', 'tempmailaddress.com', 'sharklasers.com', 'trashmail.com',
    'dispostable.com', 'emailondeck.com', 'fake-mail.ml', 'mohmal.com',
    'anonymousemail.me', 'deadaddress.com', 'mytemp.email', 'tempail.com',
    'tmpeml.com', '20minutemail.com', 'mailnesia.com', 'guerrillamailblock.com',
    'mailcatch.com', 'mailnull.com', 'neomailbox.com', 'objectmail.com',
    'proxymail.eu', 'rcpt.at', 'trash-mail.at', 'trashmail.at', 'trashmail.com',
    'trashmail.de', 'trashmail.me', 'trashmail.net', 'trashmail.org',
    'trashmail.ws', 'trashmailer.com', 'trashymail.com', 'trashymail.net'
    // ... keeping the list shorter for readability, add back the full list
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email) {
      return { isValid: false, message: 'Email is required' };
    }
    
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }
    
    const domain = email.toLowerCase().split('@')[1];
    if (BLOCKED_DOMAINS.includes(domain)) {
      return { isValid: false, message: 'Disposable email addresses are not allowed. Please use a business or personal email.' };
    }
    
    if (domain && (domain.includes('temp') || domain.includes('fake') || domain.includes('disposable'))) {
      return { isValid: false, message: 'Please use a valid business or personal email address' };
    }
    
    return { isValid: true, message: '' };
  };

  const validatePassword = (password) => {
    if (!password) {
      return { isValid: false, message: 'Password is required' };
    }
    
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long' };
    }
    
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    const strength = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
    
    if (strength < 3) {
      return { 
        isValid: false, 
        message: 'Password should contain at least 3 of: uppercase, lowercase, numbers, special characters' 
      };
    }
    
    return { isValid: true, message: 'Strong password ✓' };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let normalizedValue = value;
    if (name === 'email') {
      normalizedValue = value.toLowerCase().trim();
    }
    
    setFormData({
      ...formData,
      [name]: normalizedValue
    });
    
    if (name === 'email') {
      const validation = validateEmail(normalizedValue);
      setEmailValidation(validation);
    }
    
    if (error) {
      setError('');
    }
  };

  const checkEmailExists = async (email) => {
    try {
      const { data: authData, error: authError } = await supabase
        .from('auth.users')
        .select('email')
        .eq('email', email.toLowerCase())
        .single();
      
      if (authData) {
        return true;
      }
      
      const { data: profileData } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', email.toLowerCase())
        .single();
      
      return !!profileData;
    } catch (error) {
      logger.info('Email check error:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.country) {
        setError('Please fill in all required fields');
        return;
      }
      
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        setError(emailValidation.message);
        return;
      }
      
      setStep(2);
      setError('');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    setDebugInfo('Starting free trial signup...');

    try {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        setError(emailValidation.message);
        setIsLoading(false);
        return;
      }

      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.message);
        setIsLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setIsLoading(false);
        return;
      }

      const normalizedEmail = formData.email.toLowerCase().trim();

      const result = await auth.register(
        normalizedEmail,
        formData.password,
        `${formData.firstName} ${formData.lastName}`.trim(),
        formData.firstName,
        formData.lastName,
        formData.company,
        formData.role,
        formData.country,
        formData.phone
      );
      
      if (result.success) {
        if (result.needsConfirmation) {
          setSuccess(result.message || `🎉 Please check your email (${normalizedEmail}) and click the confirmation link to activate your account.`);
          
          localStorage.setItem('signupSuccess', 'true');
          localStorage.setItem('signupEmail', normalizedEmail);
          
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: result.message || 'Please check your email to confirm your account.',
                email: normalizedEmail
              }
            });
          }, 3000);
        } else {
          setSuccess('🎉 Account created successfully! You are now signed in.');
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        }
      } else {
        setError(result.error);
        setDebugInfo(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      logger.error('🚨 Signup error:', error);
      setDebugInfo(`🚨 Error: ${error.message}`);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Unable to connect to authentication service. Please check your internet connection and try again.');
      } else {
        setError(`Something went wrong: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true);
    setError('');
    setSuccess('');
    
    try {
      logger.info('🔄 Attempting Google OAuth signup...');
      
      const result = await auth.registerWithGoogle();

      if (result.success) {
        logger.info('✅ Google OAuth initiated successfully');
        setSuccess('🎉 Google signup initiated! You will be redirected to your dashboard shortly.');
      } else {
        logger.error('❌ Google OAuth error:', result.error);
        
        let errorMessage = 'Google sign-up failed. Please try again.';
        
        if (result.error.includes('popup')) {
          errorMessage = 'Popup blocked. Please allow popups for this site and try again.';
        } else if (result.error.includes('network')) {
          errorMessage = 'Network error during Google sign-up. Please check your connection.';
        } else if (result.error) {
          errorMessage = result.error;
        }
        
        setError(errorMessage);
      }

    } catch (error) {
      logger.error('❌ Google signup error:', error);
      setError('Google authentication error. Please try again or use email signup.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '#64748b' };
    
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    const checks = [
      password.length >= 8,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar
    ].filter(Boolean).length;
    
    if (checks < 2) return { strength: 20, label: 'Weak', color: '#ef4444' };
    if (checks < 4) return { strength: 60, label: 'Medium', color: '#f59e0b' };
    return { strength: 100, label: 'Strong', color: '#22c55e' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="signup-page">
      {/* Animated Background Elements */}
      <div className="bg-blob bg-blob-1" />
      <div className="bg-blob bg-blob-2" />

      <div className="signup-container">
        {/* Left Side - Value Proposition */}
        <div className="value-prop-section">
          <div className="value-prop-content">
            {/* Logo */}
            <Link to="/" className="logo-link">
              <span className="logo-icon">🕵️‍♂️</span>
              <span className="logo-text">WhyDidItDie</span>
            </Link>

            {/* Value Prop */}
            <h1 className="hero-title">
              Stop Killing Your Campaigns
            </h1>
            
            <p className="hero-subtitle">
              Join 10,000+ marketers who save money with AI-powered campaign forensics
            </p>

            {/* Benefits */}
            <div className="benefits-list">
              {[
                '🔍 Find exactly what killed your campaigns',
                '💸 Detect budget leaks before they drain your ROI',
                '🤖 AI-powered recommendations to fix problems',
                '⚡ Real-time alerts when things go wrong'
              ].map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            {/* Social Proof */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value stat-green">$2.4M+</div>
                <div className="stat-label">Saved in Wasted Spend</div>
              </div>
              <div className="stat-card">
                <div className="stat-value stat-orange">99%</div>
                <div className="stat-label">Issue Detection Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div className="form-section">
          <div className="form-card">
            {/* Header */}
            <div className="form-header">
              <h2 className="form-title">Start Your Free Trial</h2>
              <p className="form-subtitle">
                Full access for 2 days, no credit card required
              </p>

              {/* Free Trial Badge */}
              <div className="trial-badge">
                <span>🎁</span>
                2-Days Free Trial • No Payment Required
              </div>
            </div>

            {/* Success Message */}
            {success && (
              <div className="alert alert-success">{success}</div>
            )}

            {/* Error Message */}
            {error && (
              <div className="alert alert-error">{error}</div>
            )}

            {/* Progress Indicator */}
            <div className="progress-indicator">
              <div className={`progress-dot ${step >= 1 ? 'active' : ''}`} />
              <div className={`progress-dot ${step >= 2 ? 'active' : ''}`} />
            </div>

            {step === 1 && (
              <>
                {/* Google Sign Up Button - DISABLED */}
                <div className="google-btn-wrapper">
                  <button
                    onClick={(e) => e.preventDefault()}
                    disabled={true}
                    className="google-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                  <div className="coming-soon-badge">Coming Soon</div>
                </div>

                {/* Divider */}
                <div className="divider">
                  <div className="divider-line"></div>
                  <span className="divider-text">or sign up with email</span>
                  <div className="divider-line"></div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="signup-form">
              {step === 1 ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                        placeholder="John"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Business Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`form-input ${!emailValidation.isValid && formData.email ? 'input-error' : ''}`}
                      placeholder="john@company.com"
                    />
                    {formData.email && !emailValidation.isValid && (
                      <div className="field-error">
                        <span>⚠️</span>
                        {emailValidation.message}
                      </div>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country *</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                        placeholder="Enter your country"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Your Company (optional)"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className="form-input form-select"
                      >
                        <option value="">Select role (optional)</option>
                        <option value="Marketing Manager">Marketing Manager</option>
                        <option value="Digital Marketer">Digital Marketer</option>
                        <option value="Marketing Director">Marketing Director</option>
                        <option value="CMO">CMO</option>
                        <option value="Founder/CEO">Founder/CEO</option>
                        <option value="Agency Owner">Agency Owner</option>
                        <option value="Consultant">Consultant</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <div className="input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                        placeholder="Create a strong password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="toggle-password"
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    
                    {formData.password && (
                      <div className="password-strength">
                        <div className="strength-header">
                          <span className="strength-label" style={{ color: passwordStrength.color }}>
                            {passwordStrength.label} Password
                          </span>
                          <span className="strength-percent">{passwordStrength.strength}%</span>
                        </div>
                        <div className="strength-bar">
                          <div
                            className="strength-fill"
                            style={{ 
                              width: `${passwordStrength.strength}%`,
                              backgroundColor: passwordStrength.color
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Confirm Password *</label>
                    <div className="input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        required
                        className={`form-input ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'input-error' : ''}`}
                        placeholder="Confirm your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="toggle-password"
                      >
                        {showConfirmPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <div className="field-error">
                        <span>⚠️</span>
                        Passwords do not match
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Navigation Buttons */}
              <div className="button-row">
                {step === 2 && (
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="btn btn-secondary"
                  >
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isLoading || isGoogleLoading || (step === 1 && !emailValidation.isValid)}
                  className={`btn btn-primary ${step === 1 ? 'btn-full' : 'btn-wide'}`}
                >
                  {isLoading ? 'Creating Your Free Trial...' : (step === 1 ? 'Continue' : 'Start Free Trial')}
                </button>
              </div>

              {/* Meta Policy Compliance */}
              <p className="policy-text">
                By continuing, you agree to our{' '}
                <a href="https://app.whydiditdie.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                {' '}and{' '}
                <a href="https://app.whydiditdie.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
              </p>

              {/* Login Link */}
              <div className="login-link">
                <span>Already have an account?{' '}</span>
                <Link to="/login">Sign in here</Link>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        /* Reset and Base */
        .signup-page {
          min-height: 100vh;
          min-height: 100dvh;
          background: #000;
          position: relative;
          overflow-x: hidden;
        }

        /* Background Blobs */
        .bg-blob {
          position: fixed;
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
          z-index: 0;
        }

        .bg-blob-1 {
          top: 5%;
          left: 5%;
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #e07a5f, #ff6b6b);
          opacity: 0.08;
          animation: float 12s ease-in-out infinite;
        }

        .bg-blob-2 {
          bottom: 5%;
          right: 5%;
          width: 350px;
          height: 350px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          opacity: 0.08;
          animation: float 15s ease-in-out infinite reverse;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(180deg); }
        }

        /* Main Container */
        .signup-container {
          display: flex;
          min-height: 100vh;
          min-height: 100dvh;
          position: relative;
          z-index: 1;
        }

        /* Left Section - Value Prop */
        .value-prop-section {
          flex: 1;
          display: none;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        @media (min-width: 1024px) {
          .value-prop-section {
            display: flex;
          }
        }

        .value-prop-content {
          max-width: 480px;
          text-align: center;
        }

        .logo-link {
          display: inline-flex;
          align-items: center;
          gap: 1rem;
          text-decoration: none;
          margin-bottom: 2rem;
        }

        .logo-icon {
          font-size: 3rem;
        }

        .logo-text {
          font-size: 2.5rem;
          font-weight: 700;
          color: #e07a5f;
        }

        .hero-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 900;
          margin-bottom: 1rem;
          background: linear-gradient(90deg, #fff, #e07a5f, #ff6b6b);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .benefits-list {
          text-align: left;
          margin-bottom: 2rem;
        }

        .benefit-item {
          display: flex;
          align-items: center;
          padding: 0.75rem 1rem;
          margin-bottom: 0.75rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.5rem;
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.95rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .stat-card {
          padding: 1.25rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.75rem;
          text-align: center;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
        }

        .stat-green {
          color: #10b981;
        }

        .stat-orange {
          color: #e07a5f;
        }

        .stat-label {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.7);
          margin-top: 0.25rem;
        }

        /* Right Section - Form */
        .form-section {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          overflow-y: auto;
        }

        .form-card {
          width: 100%;
          max-width: 450px;
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1.5rem;
          backdrop-filter: blur(20px);
        }

        @media (min-width: 768px) {
          .form-card {
            padding: 2rem;
          }
        }

        .form-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .form-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
          margin: 0 0 0.5rem;
        }

        .form-subtitle {
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.7);
          margin: 0 0 1rem;
        }

        .trial-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(16, 185, 129, 0.2);
          border: 1px solid rgba(16, 185, 129, 0.4);
          border-radius: 9999px;
          color: #10b981;
          font-size: 0.875rem;
          font-weight: 600;
        }

        /* Alerts */
        .alert {
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          word-break: break-word;
        }

        .alert-success {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: #10b981;
        }

        .alert-error {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }

        /* Progress Indicator */
        .progress-indicator {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .progress-dot {
          width: 2.5rem;
          height: 0.25rem;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 2px;
          transition: background 0.3s;
        }

        .progress-dot.active {
          background: #10b981;
        }

        /* Google Button */
        .google-btn-wrapper {
          position: relative;
          margin-bottom: 1.5rem;
        }

        .google-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 0.5rem;
          color: rgba(255, 255, 255, 0.5);
          font-size: 1rem;
          font-weight: 600;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .coming-soon-badge {
          position: absolute;
          top: -0.5rem;
          right: -0.5rem;
          padding: 0.25rem 0.5rem;
          background: #eab308;
          color: #000;
          font-size: 0.7rem;
          font-weight: 700;
          border-radius: 9999px;
        }

        /* Divider */
        .divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .divider-line {
          flex: 1;
          height: 1px;
          background: rgba(255, 255, 255, 0.2);
        }

        .divider-text {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.6);
          white-space: nowrap;
        }

        /* Form */
        .signup-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-row {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        @media (min-width: 480px) {
          .form-row {
            flex-direction: row;
          }
          
          .form-row .form-group {
            flex: 1;
          }
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-label {
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }

        .form-input {
          width: 100%;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 0.5rem;
          color: #fff;
          font-size: 1rem;
          outline: none;
          transition: border-color 0.2s, background 0.2s;
        }

        .form-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        .form-input:focus {
          border-color: #10b981;
          background: rgba(255, 255, 255, 0.15);
        }

        .form-input.input-error {
          border-color: rgba(239, 68, 68, 0.5);
        }

        .form-select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          background-size: 1rem;
          padding-right: 2.5rem;
        }

        .form-select option {
          background: #1f2937;
          color: #fff;
        }

        .input-wrapper {
          position: relative;
        }

        .toggle-password {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          font-size: 0.875rem;
          padding: 0;
        }

        .field-error {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: #ef4444;
        }

        /* Password Strength */
        .password-strength {
          margin-top: 0.5rem;
        }

        .strength-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.25rem;
        }

        .strength-label {
          font-size: 0.75rem;
          font-weight: 500;
        }

        .strength-percent {
          font-size: 0.65rem;
          color: rgba(255, 255, 255, 0.5);
        }

        .strength-bar {
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 2px;
          overflow: hidden;
        }

        .strength-fill {
          height: 100%;
          transition: width 0.3s, background-color 0.3s;
        }

        /* Buttons */
        .button-row {
          display: flex;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .btn {
          padding: 1rem;
          border-radius: 0.5rem;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          flex: 1;
          background: #10b981;
          border: none;
          color: #fff;
        }

        .btn-primary:hover:not(:disabled) {
          background: #059669;
          transform: translateY(-2px);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          flex: 1;
          background: transparent;
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: #fff;
        }

        .btn-secondary:hover {
          border-color: rgba(255, 255, 255, 0.5);
        }

        .btn-full {
          width: 100%;
        }

        .btn-wide {
          flex: 2;
        }

        /* Policy & Links */
        .policy-text {
          text-align: center;
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.5);
          margin-top: 1rem;
        }

        .policy-text a {
          color: #3b82f6;
          text-decoration: none;
        }

        .policy-text a:hover {
          text-decoration: underline;
        }

        .login-link {
          text-align: center;
          padding-top: 1rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .login-link a {
          color: #10b981;
          font-weight: 700;
          text-decoration: none;
        }

        .login-link a:hover {
          text-decoration: underline;
        }

        /* Tablet Responsive */
        @media (max-width: 768px) {
          .form-section {
            padding: 1rem;
            align-items: flex-start;
            padding-top: 2rem;
            padding-bottom: 2rem;
          }

          .form-card {
            max-width: 100%;
          }

          .trial-badge {
            font-size: 0.75rem;
            padding: 0.4rem 0.75rem;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          .signup-page {
            min-height: 100dvh;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          .form-section {
            padding: 0.75rem;
            padding-top: 1rem;
            padding-bottom: 3rem;
          }

          .form-card {
            padding: 1.25rem;
            border-radius: 1rem;
            border: none;
            background: rgba(255, 255, 255, 0.03);
          }

          .form-header {
            margin-bottom: 1.25rem;
          }

          .form-title {
            font-size: 1.35rem;
          }

          .form-subtitle {
            font-size: 0.85rem;
            margin-bottom: 0.75rem;
          }

          .trial-badge {
            font-size: 0.7rem;
            padding: 0.35rem 0.65rem;
          }

          .progress-indicator {
            margin-bottom: 1.25rem;
          }

          .google-btn-wrapper {
            margin-bottom: 1.25rem;
          }

          .google-btn {
            padding: 0.875rem;
            font-size: 0.9rem;
          }

          .divider {
            margin-bottom: 1.25rem;
          }

          .divider-text {
            font-size: 0.8rem;
          }

          .signup-form {
            gap: 0.875rem;
          }

          .form-row {
            gap: 0.875rem;
          }

          .form-label {
            font-size: 0.8rem;
            margin-bottom: 0.4rem;
          }

          /* Larger touch targets for mobile */
          .form-input {
            font-size: 16px; /* Prevents iOS zoom */
            padding: 0.875rem;
            min-height: 48px; /* Minimum touch target */
            border-radius: 0.625rem;
          }

          .form-select {
            min-height: 48px;
          }

          .toggle-password {
            padding: 0.5rem;
            font-size: 1rem;
          }

          .btn {
            padding: 0.875rem;
            min-height: 50px; /* Larger touch target */
            font-size: 0.95rem;
            border-radius: 0.625rem;
          }

          .button-row {
            gap: 0.75rem;
            margin-top: 0.75rem;
          }

          .policy-text {
            font-size: 0.7rem;
            margin-top: 0.875rem;
            line-height: 1.5;
          }

          .login-link {
            padding-top: 0.875rem;
            font-size: 0.8rem;
          }

          .alert {
            padding: 0.75rem;
            font-size: 0.8rem;
            border-radius: 0.5rem;
          }

          .field-error {
            font-size: 0.7rem;
          }
        }

        /* Extra small devices */
        @media (max-width: 360px) {
          .form-section {
            padding: 0.5rem;
            padding-top: 0.75rem;
          }

          .form-card {
            padding: 1rem;
            border-radius: 0.75rem;
          }

          .form-title {
            font-size: 1.2rem;
          }

          .form-subtitle {
            font-size: 0.8rem;
          }

          .trial-badge {
            font-size: 0.65rem;
          }

          .form-input {
            padding: 0.75rem;
          }

          .btn {
            padding: 0.75rem;
            font-size: 0.9rem;
          }

          .form-row {
            flex-direction: column;
          }
        }

        /* Landscape mobile */
        @media (max-height: 500px) and (orientation: landscape) {
          .form-section {
            padding: 0.5rem 1rem;
            align-items: flex-start;
          }

          .form-card {
            padding: 1rem 1.5rem;
          }

          .form-header {
            margin-bottom: 0.75rem;
          }

          .form-title {
            font-size: 1.1rem;
            margin-bottom: 0.25rem;
          }

          .form-subtitle {
            font-size: 0.75rem;
            margin-bottom: 0.5rem;
          }

          .trial-badge {
            display: none;
          }

          .progress-indicator {
            margin-bottom: 0.75rem;
          }

          .signup-form {
            gap: 0.5rem;
          }

          .form-row {
            flex-direction: row;
            gap: 0.75rem;
          }

          .form-label {
            font-size: 0.7rem;
            margin-bottom: 0.25rem;
          }

          .form-input {
            padding: 0.5rem 0.75rem;
            min-height: 40px;
          }

          .btn {
            padding: 0.6rem;
            min-height: 42px;
          }

          .button-row {
            margin-top: 0.5rem;
          }

          .policy-text {
            margin-top: 0.5rem;
          }

          .login-link {
            padding-top: 0.5rem;
          }
        }

        /* Safe area insets for notched phones */
        @supports (padding: max(0px)) {
          .signup-page {
            padding-left: max(0px, env(safe-area-inset-left));
            padding-right: max(0px, env(safe-area-inset-right));
            padding-bottom: max(0px, env(safe-area-inset-bottom));
          }
        }

        /* Reduce motion for accessibility */
        @media (prefers-reduced-motion: reduce) {
          .bg-blob {
            animation: none;
          }

          .btn,
          .form-input,
          .progress-dot,
          .strength-fill {
            transition: none;
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .form-card {
            border: 2px solid rgba(255, 255, 255, 0.5);
          }

          .form-input {
            border-width: 2px;
          }

          .btn-primary {
            background: #0d9488;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          .btn:hover {
            transform: none;
          }

          .form-input:focus {
            outline: 2px solid #10b981;
            outline-offset: 2px;
          }

          /* Prevent double-tap zoom */
          .btn,
          .toggle-password,
          .form-input,
          .form-select {
            touch-action: manipulation;
          }
        }
      `}</style>
    </div>
  );
};

export default Signup;